import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { analyticsAPI, profileAPI } from '@/lib/api';
import { toast } from 'sonner';
import {
  AreaChart, Area, BarChart, Bar, ResponsiveContainer,
  XAxis, YAxis, Tooltip, Cell, PieChart, Pie, Legend
} from 'recharts';
import {
  Eye, Users, Download, MessageCircle, Phone, Mail, Globe,
  MapPin, BarChart2, TrendingUp, Smartphone, Monitor, Tablet,
  Share2, ExternalLink, ChevronLeft, ChevronRight, Activity
} from 'lucide-react';

const CHART_COLORS = ['#7c3aed', '#a855f7', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#f97316'];

const EVENT_LABELS = {
  profile_view: 'Profile View',
  vcard_download: 'Contact Saved',
  whatsapp_click: 'WhatsApp',
  phone_click: 'Phone Call',
  email_click: 'Email',
  website_click: 'Website',
  address_click: 'Maps',
  social_click: 'Social Link',
};

const EVENT_COLORS = {
  profile_view: '#7c3aed',
  vcard_download: '#10b981',
  whatsapp_click: '#22c55e',
  phone_click: '#f59e0b',
  email_click: '#3b82f6',
  website_click: '#06b6d4',
  address_click: '#ef4444',
  social_click: '#a855f7',
};

function timeAgo(ts) {
  if (!ts) return '—';
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const FLAG_EMOJI = (code) => {
  if (!code || code.length !== 2) return '🌍';
  return String.fromCodePoint(...[...code.toUpperCase()].map(c => 0x1F1E6 + c.charCodeAt(0) - 65));
};

const StatCard = ({ icon: Icon, label, value, color, sub }) => (
  <motion.div
    className="glass rounded-2xl p-5"
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
  >
    <div className="flex items-start justify-between mb-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center`} style={{ background: `${color}20` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <span className="text-2xl font-bold text-white">{value.toLocaleString()}</span>
    </div>
    <p className="text-slate-400 text-sm">{label}</p>
    {sub && <p className="text-xs mt-1" style={{ color }}>{sub}</p>}
  </motion.div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass rounded-xl px-3 py-2 text-xs">
      <p className="text-slate-400">{label}</p>
      <p className="text-white font-semibold">{payload[0]?.value} {payload[0]?.name || 'views'}</p>
    </div>
  );
};

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [slug, setSlug] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Recent Activity state
  const [events, setEvents] = useState([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [eventsTotalPages, setEventsTotalPages] = useState(1);
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsFilter, setEventsFilter] = useState(null);

  // Audience table pagination
  const [locationsVisible, setLocationsVisible] = useState(5);
  const [browsersVisible, setBrowsersVisible] = useState(5); // null = all

  useEffect(() => {
    const load = async () => {
      try {
        const profileResp = await profileAPI.get();
        const profileSlug = profileResp.data?.profile?.slug;
        if (!profileSlug) {
          setLoading(false);
          return;
        }
        setSlug(profileSlug);
        const analyticsResp = await analyticsAPI.get(profileSlug);
        setData(analyticsResp.data);
      } catch (e) {
        toast.error('Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const loadEvents = useCallback(async (currentSlug, page, filter) => {
    if (!currentSlug) return;
    setEventsLoading(true);
    try {
      const resp = await analyticsAPI.getEvents(currentSlug, page, 15, filter);
      setEvents(resp.data.events || []);
      setEventsTotal(resp.data.total || 0);
      setEventsTotalPages(resp.data.totalPages || 1);
    } catch {
      // silently fail — events table is non-critical
    } finally {
      setEventsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (slug) loadEvents(slug, eventsPage, eventsFilter);
  }, [slug, eventsPage, eventsFilter, loadEvents]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!slug) return (
    <div className="max-w-lg mx-auto text-center py-20" data-testid="analytics-no-profile">
      <BarChart2 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
      <h2 className="text-xl font-bold text-white mb-2">No Profile Yet</h2>
      <p className="text-slate-400">Create your public profile first to start collecting analytics.</p>
    </div>
  );

  const s = data?.summary || {};
  const peakHour = data?.viewsByHour?.reduce((max, h) => h.views > max.views ? h : max, { hour: 0, views: 0 });
  const topCountry = data?.locationBreakdown?.[0];

  const deviceIcon = (d) => d === 'Mobile' ? Smartphone : d === 'Tablet' ? Tablet : Monitor;

  return (
    <div className="space-y-6 pb-12" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Profile Analytics</h1>
          <p className="text-slate-400 text-sm mt-1">
            <span className="text-violet-400">@{slug}</span> · All time data
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a
            href={`/share/${slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:scale-[1.02]"
            style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}
            data-testid="share-snapshot-link"
          >
            <Share2 className="w-4 h-4" />
            <span className="hidden sm:inline">Share My Stats</span>
            <span className="sm:hidden">Share</span>
          </a>
          <a
            href={`/p/${slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary flex items-center gap-2 text-sm px-3 py-2 rounded-xl"
            data-testid="view-public-profile-btn"
          >
            <ExternalLink className="w-4 h-4" />
            <span className="hidden sm:inline">View Profile</span>
          </a>
        </div>
      </div>

      {/* Tabs — horizontally scrollable on mobile */}
      <div className="overflow-x-auto -mx-1 px-1">
        <div className="flex gap-1 border-b border-slate-800 min-w-max">
          {['overview', 'engagement', 'audience'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2.5 text-sm font-medium capitalize transition-colors border-b-2 -mb-px whitespace-nowrap ${
                activeTab === tab
                  ? 'text-violet-400 border-violet-500'
                  : 'text-slate-500 border-transparent hover:text-slate-300'
              }`}
              data-testid={`analytics-tab-${tab}`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'overview' && (
        <div className="space-y-8">
          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Eye} label="Total Views" value={s.totalViews || 0} color="#7c3aed"
              sub={`${s.uniqueVisitors || 0} unique visitors`} />
            <StatCard icon={Download} label="Contact Saves" value={s.vcardDownloads || 0} color="#10b981"
              sub="vCard downloads" />
            <StatCard icon={MessageCircle} label="WhatsApp" value={s.whatsappClicks || 0} color="#22c55e"
              sub="chat taps" />
            <StatCard icon={TrendingUp} label="Total Actions" value={s.totalActions || 0} color="#a855f7"
              sub="all interactions" />
          </div>

          {/* Views over 30 days */}
          <motion.div className="glass rounded-2xl p-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h3 className="text-white font-semibold mb-6">Views — Last 30 Days</h3>
            {(s.totalViews || 0) === 0 ? (
              <div className="h-40 flex items-center justify-center text-slate-500 text-sm">
                No view data yet. Share your profile to get started!
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={data?.viewsByDay || []} margin={{ left: -20 }}>
                  <defs>
                    <linearGradient id="viewGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#7c3aed" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 11 }} tickLine={false} axisLine={false}
                    interval={Math.floor((data?.viewsByDay?.length || 30) / 6)} />
                  <YAxis tick={{ fill: '#64748b', fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="views" stroke="#7c3aed" strokeWidth={2}
                    fill="url(#viewGrad)" dot={false} activeDot={{ r: 4, fill: '#a855f7' }} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </motion.div>

          {/* Peak hours + quick stats */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <motion.div className="glass rounded-2xl p-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <h3 className="text-white font-semibold mb-1">Peak Hours</h3>
              <p className="text-slate-500 text-xs mb-5">
                {peakHour?.views > 0
                  ? `Most activity at ${peakHour.label} (${peakHour.views} views)`
                  : 'No data yet'}
              </p>
              <ResponsiveContainer width="100%" height={150}>
                <BarChart data={data?.viewsByHour || []} margin={{ left: -28 }}>
                  <XAxis dataKey="hour" tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false}
                    tickFormatter={h => h % 6 === 0 ? `${h}h` : ''} />
                  <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="views" radius={[3, 3, 0, 0]}>
                    {(data?.viewsByHour || []).map((entry, i) => (
                      <Cell key={i} fill={entry.hour === peakHour?.hour ? '#a855f7' : '#7c3aed40'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </motion.div>

            {/* Quick insight cards */}
            <div className="grid grid-cols-2 gap-4">
              <StatCard icon={Phone} label="Phone Clicks" value={s.phoneClicks || 0} color="#f59e0b" />
              <StatCard icon={Mail} label="Email Clicks" value={s.emailClicks || 0} color="#3b82f6" />
              <StatCard icon={Globe} label="Website Visits" value={s.websiteClicks || 0} color="#10b981" />
              <StatCard icon={MapPin} label="Maps Opens" value={s.addressClicks || 0} color="#ef4444" />
            </div>
          </div>

          {/* Recent Activity Table */}
          <motion.div className="glass rounded-2xl p-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-violet-400" />
                <h3 className="text-white font-semibold">Recent Activity</h3>
                {eventsTotal > 0 && (
                  <span className="text-xs text-slate-500 bg-slate-800 px-2 py-0.5 rounded-full">
                    {eventsTotal.toLocaleString()} total
                  </span>
                )}
              </div>
              {/* Filter pills */}
              <div className="flex items-center gap-1 flex-wrap">
                {[null, 'profile_view', 'vcard_download', 'whatsapp_click', 'social_click'].map(f => (
                  <button
                    key={f ?? 'all'}
                    onClick={() => { setEventsFilter(f); setEventsPage(1); }}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                      eventsFilter === f
                        ? 'bg-violet-600 text-white'
                        : 'bg-slate-800 text-slate-400 hover:text-slate-200'
                    }`}
                    data-testid={`events-filter-${f ?? 'all'}`}
                  >
                    {f ? EVENT_LABELS[f] : 'All'}
                  </button>
                ))}
              </div>
            </div>

            {eventsLoading ? (
              <div className="h-40 flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : events.length === 0 ? (
              <div className="h-32 flex items-center justify-center text-slate-500 text-sm">
                No events yet. Share your profile to start collecting data!
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="recent-activity-table">
                    <thead>
                      <tr className="border-b border-slate-800">
                        <th className="text-left text-slate-500 font-medium pb-3 pr-4">Time</th>
                        <th className="text-left text-slate-500 font-medium pb-3 pr-4">Event</th>
                        <th className="text-left text-slate-500 font-medium pb-3 pr-4">Device</th>
                        <th className="text-left text-slate-500 font-medium pb-3">Location</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.map((e, i) => (
                        <tr key={i} className="border-b border-slate-800/40 hover:bg-slate-800/20 transition-colors">
                          <td className="py-2.5 pr-4 text-slate-400 text-xs whitespace-nowrap">{timeAgo(e.timestamp)}</td>
                          <td className="py-2.5 pr-4">
                            <span
                              className="px-2 py-0.5 rounded-full text-xs font-medium"
                              style={{
                                background: `${EVENT_COLORS[e.eventType] || '#7c3aed'}18`,
                                color: EVENT_COLORS[e.eventType] || '#a855f7'
                              }}
                            >
                              {EVENT_LABELS[e.eventType] || e.eventType}
                            </span>
                          </td>
                          <td className="py-2.5 pr-4 text-slate-300 text-xs capitalize">{e.device || '—'}</td>
                          <td className="py-2.5 text-slate-300 text-xs">
                            {e.country
                              ? <>{FLAG_EMOJI(e.countryCode)} {e.country}</>
                              : <span className="text-slate-600">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {eventsTotalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-800">
                    <button
                      onClick={() => setEventsPage(p => Math.max(1, p - 1))}
                      disabled={eventsPage === 1}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                      data-testid="events-prev-btn"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" /> Prev
                    </button>
                    <span className="text-slate-500 text-xs">
                      Page {eventsPage} of {eventsTotalPages}
                    </span>
                    <button
                      onClick={() => setEventsPage(p => Math.min(eventsTotalPages, p + 1))}
                      disabled={eventsPage === eventsTotalPages}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                      data-testid="events-next-btn"
                    >
                      Next <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </>
            )}
          </motion.div>
        </div>
      )}

      {activeTab === 'engagement' && (
        <div className="space-y-8">
          {/* Actions breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <motion.div className="glass rounded-2xl p-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <h3 className="text-white font-semibold mb-6">Actions Breakdown</h3>
              {!data?.actionsBreakdown?.length ? (
                <div className="h-40 flex items-center justify-center text-slate-500 text-sm">No action data yet</div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={data.actionsBreakdown} dataKey="count" nameKey="label"
                      cx="50%" cy="50%" outerRadius={80} innerRadius={45} paddingAngle={3}>
                      {data.actionsBreakdown.map((_, i) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v, n) => [v, n]} contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, fontSize: 12 }} />
                    <Legend iconType="circle" iconSize={8} formatter={(v) => <span style={{ color: '#94a3b8', fontSize: 12 }}>{v}</span>} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </motion.div>

            {/* Social links */}
            <motion.div className="glass rounded-2xl p-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <h3 className="text-white font-semibold mb-6">Social Link Clicks</h3>
              {!data?.socialLinkPerformance?.length ? (
                <div className="h-40 flex items-center justify-center text-slate-500 text-sm">No social click data yet</div>
              ) : (
                <div className="space-y-3">
                  {data.socialLinkPerformance.map((item, i) => {
                    const pct = Math.round((item.clicks / (data.summary?.socialClicks || 1)) * 100);
                    return (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm capitalize text-slate-300">{item.platform}</span>
                          <span className="text-sm font-semibold text-white">{item.clicks}</span>
                        </div>
                        <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-700"
                            style={{ width: `${pct}%`, background: CHART_COLORS[i % CHART_COLORS.length] }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          </div>

          {/* All actions as a horizontal bar chart */}
          <motion.div className="glass rounded-2xl p-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h3 className="text-white font-semibold mb-6">All Engagement Actions</h3>
            {!data?.actionsBreakdown?.length ? (
              <div className="h-32 flex items-center justify-center text-slate-500 text-sm">No data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(160, data.actionsBreakdown.length * 40)}>
                <BarChart data={data.actionsBreakdown} layout="vertical" margin={{ left: 20, right: 20 }}>
                  <XAxis type="number" tick={{ fill: '#64748b', fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <YAxis type="category" dataKey="label" tick={{ fill: '#94a3b8', fontSize: 12 }} tickLine={false} axisLine={false} width={110} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {data.actionsBreakdown.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </motion.div>
        </div>
      )}

      {activeTab === 'audience' && (
        <div className="space-y-8">
          {/* Location + Device */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top locations */}
            <motion.div className="glass rounded-2xl p-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-white font-semibold">Top Locations</h3>
                {data?.locationBreakdown?.length > 5 && (
                  <span className="text-slate-600 text-xs">{data.locationBreakdown.length} countries</span>
                )}
              </div>
              <p className="text-slate-500 text-xs mb-5">
                {topCountry ? `${topCountry.country} leads with ${topCountry.count} views` : 'No geo data yet (may take a few minutes)'}
              </p>
              {!data?.locationBreakdown?.length ? (
                <div className="h-48 flex items-center justify-center text-slate-500 text-sm">
                  Location data appears after a few visits. Share your profile!
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    {data.locationBreakdown.slice(0, locationsVisible).map((loc, i) => {
                      const pct = Math.round((loc.count / (s.totalViews || 1)) * 100);
                      return (
                        <div key={i}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm text-slate-300 flex items-center gap-2">
                              <span>{FLAG_EMOJI(loc.countryCode)}</span>
                              {loc.country}
                            </span>
                            <span className="text-sm text-slate-400">{loc.count} <span className="text-slate-600 text-xs">({pct}%)</span></span>
                          </div>
                          <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: CHART_COLORS[i % 3] }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {data.locationBreakdown.length > 5 && (
                    <button
                      onClick={() => setLocationsVisible(v => v === 5 ? data.locationBreakdown.length : 5)}
                      className="mt-4 text-xs text-violet-400 hover:text-violet-300 transition-colors"
                      data-testid="locations-toggle-btn"
                    >
                      {locationsVisible === 5
                        ? `Show ${data.locationBreakdown.length - 5} more countries`
                        : 'Show less'}
                    </button>
                  )}
                </>
              )}
            </motion.div>

            {/* Device + Browser split */}
            <div className="space-y-4">
              <motion.div className="glass rounded-2xl p-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <h3 className="text-white font-semibold mb-4">Devices</h3>
                {!data?.deviceBreakdown?.length ? (
                  <p className="text-slate-500 text-sm">No data yet</p>
                ) : (
                  <div className="space-y-3">
                    {data.deviceBreakdown.map((d, i) => {
                      const Icon = deviceIcon(d.device);
                      const pct = Math.round((d.count / (s.totalViews || 1)) * 100);
                      return (
                        <div key={i} className="flex items-center gap-3">
                          <Icon className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1 text-sm">
                              <span className="text-slate-300">{d.device}</span>
                              <span className="text-slate-400">{pct}%</span>
                            </div>
                            <div className="h-1.5 bg-slate-800 rounded-full">
                              <div className="h-full rounded-full" style={{ width: `${pct}%`, background: CHART_COLORS[i] }} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </motion.div>

              <motion.div className="glass rounded-2xl p-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-semibold">Browsers</h3>
                  {data?.browserBreakdown?.length > 5 && (
                    <span className="text-slate-600 text-xs">{data.browserBreakdown.length} total</span>
                  )}
                </div>
                {!data?.browserBreakdown?.length ? (
                  <p className="text-slate-500 text-sm">No data yet</p>
                ) : (
                  <>
                    <div className="space-y-2">
                      {data.browserBreakdown.slice(0, browsersVisible).map((b, i) => {
                        const pct = Math.round((b.count / (s.totalViews || 1)) * 100);
                        return (
                          <div key={i}>
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="text-slate-300">{b.browser}</span>
                              <span className="text-slate-400">{b.count} <span className="text-slate-600 text-xs">({pct}%)</span></span>
                            </div>
                            <div className="h-1 bg-slate-800 rounded-full overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${pct}%`, background: CHART_COLORS[i % CHART_COLORS.length] }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {data.browserBreakdown.length > 5 && (
                      <button
                        onClick={() => setBrowsersVisible(v => v === 5 ? data.browserBreakdown.length : 5)}
                        className="mt-3 text-xs text-violet-400 hover:text-violet-300 transition-colors"
                        data-testid="browsers-toggle-btn"
                      >
                        {browsersVisible === 5
                          ? `Show ${data.browserBreakdown.length - 5} more`
                          : 'Show less'}
                      </button>
                    )}
                  </>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
